/** EVERY LIST'S SORT (ported 2026-10-18 from the medrec console's lib/list-sort.ts, its design plan's Lane 1; Rod there: "i will need to be
 *  able to sort each column"; DESIGN_SYSTEM 7a). Pure, no React. Every data column of every list sorts; the sort lives in the address
 *  (`?sort=<column>&dir=asc|desc`, one pair per list), so it survives a refresh and a link reproduces it; the rows sort INSIDE their groups
 *  (a group's rows stay together, the groups keep their order); a press on another column sorts by it ascending, a second press on the same
 *  column flips it; "Clear the sort" goes back to the list's default. The sort is stable: rows that tie keep the order they came in, and an
 *  empty value sits after every value in both directions. Each list names its own columns and default (`ListSortSpec`) and draws its headers
 *  with `list/SortHeader.tsx`.
 *
 *  CLAIMS' DEFAULT IS USUALLY ITS OWN ORDER (a `null` column): the Queue's rows come ranked from core todo.ts (urgent first in each group),
 *  Today's newest first; a column sort is a person's choice on top of that, and "Clear the sort" gives the ranking back. A server-paged list
 *  (All unopened, 100 a page) sends the same pair to the server, which sorts the whole list before it pages (core unopened.ts, 10-18). */

export type SortDir = 'asc' | 'desc';
/** A list's sort: the column (null: the list's own order, no column) and the direction. */
export interface ListSort<K extends string> { key: K | null; dir: SortDir }
/** A list's sortable columns, its default sort, and each column's words for the "Sorted by" line, both directions ("oldest first"). */
export interface ListSortSpec<K extends string> {
  keys: readonly K[];
  default: ListSort<K>;
  /** The column's name ("Since") and what each direction means for it ("oldest first", "newest first"). */
  words: Readonly<Record<K, { name: string; asc: string; desc: string }>>;
}
/** The two address keys every list's sort uses. */
export const SORT_PARAM = 'sort';
export const DIR_PARAM = 'dir';

/** THE SORT AN ADDRESS SAYS: a known column with its direction (`asc` unless `desc`), else the list's default. A `dir` with no `sort` is the
 *  default's column in that direction. */
export function sortOf<K extends string>(params: URLSearchParams, spec: ListSortSpec<K>): ListSort<K> {
  const raw = params.get(SORT_PARAM);
  const key = spec.keys.find((k) => k === raw) ?? null;
  const dirRaw = params.get(DIR_PARAM);
  if (key === null) return dirRaw === 'asc' || dirRaw === 'desc' ? { key: spec.default.key, dir: dirRaw } : spec.default;
  return { key, dir: dirRaw === 'desc' ? 'desc' : 'asc' };
}
/** THE ADDRESS FOR A SORT: every other key kept in its order; `sort` and `dir` only when the sort is not the list's default. */
export function writeSort<K extends string>(params: URLSearchParams, s: ListSort<K>, spec: ListSortSpec<K>): URLSearchParams {
  const out = new URLSearchParams();
  params.forEach((v, k) => { if (k !== SORT_PARAM && k !== DIR_PARAM) out.append(k, v); });
  if (isDefault(s, spec)) return out;
  if (s.key !== null) out.append(SORT_PARAM, s.key);
  out.append(DIR_PARAM, s.dir);
  return out;
}
export const isDefault = <K extends string>(s: ListSort<K>, spec: ListSortSpec<K>): boolean => s.key === spec.default.key && s.dir === spec.default.dir;
/** A HEADER PRESSED: another column sorts by it ascending; the same column flips. */
export function pressSort<K extends string>(s: ListSort<K>, key: K): ListSort<K> {
  return s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' };
}

/** The sort's text rule: case and accents aside, numbers as numbers ("2" before "10"). */
const COLLATOR = new Intl.Collator('en-US', { sensitivity: 'base', numeric: true });
export type SortValue = string | number | null;
/** Two values in ascending order; an empty one (null, or an empty string) after every value. */
export function compareSortValues(a: SortValue, b: SortValue): number {
  const ea = a === null || a === '', eb = b === null || b === '';
  if (ea || eb) return ea === eb ? 0 : ea ? 1 : -1;
  return typeof a === 'number' && typeof b === 'number' ? a - b : COLLATOR.compare(String(a), String(b));
}

/** THE ROWS SORTED, INSIDE THEIR GROUPS: each group's rows stay together, the groups in the order `groupOf` ranks them (a smaller rank
 *  first; absent, one group), and inside each the rows by the column's value in the direction, an empty value last in both directions, a tie
 *  in the order given. With no column the order given stands (grouped). */
export function sortRows<R, K extends string>(
  rows: readonly R[], s: ListSort<K>, valueOf: (row: R, key: K) => SortValue, groupOf?: (row: R) => number,
): R[] {
  const sign = s.dir === 'asc' ? 1 : -1;
  return rows
    .map((row, at) => ({ row, at, g: groupOf?.(row) ?? 0, v: s.key === null ? null : valueOf(row, s.key) }))
    .sort((x, y) => {
      if (x.g !== y.g) return x.g - y.g;
      if (s.key === null) return x.at - y.at;
      const ex = x.v === null || x.v === '', ey = y.v === null || y.v === '';
      if (ex || ey) return ex === ey ? x.at - y.at : ex ? 1 : -1;
      return sign * compareSortValues(x.v, y.v) || x.at - y.at;
    })
    .map((x) => x.row);
}

/** THE SORT'S WORDS (the toolbar's grey line and each header's hover). */
export const SORT_WORDS = Object.freeze({
  /** "Sorted by Since, oldest first, inside each group" (`where` "group"), "..., inside this page" ("page"), else without the last part. */
  sortedBy: (name: string, how: string, where: 'group' | 'page' | null): string =>
    `Sorted by ${name}, ${how}${where === 'group' ? ', inside each group' : where === 'page' ? ', inside this page' : ''}`,
  clear: 'Clear the sort',
  /** A header's hover. */
  sortBy: (name: string): string => `Sort by ${name}`,
});
/** The toolbar's line for a sort, or null for the list's own order with no column. `grouped`: true inside each group, 'page' inside the
 *  page on screen (a server-paged list), false for a flat list. */
export function sortedLine<K extends string>(s: ListSort<K>, spec: ListSortSpec<K>, grouped: boolean | 'page'): string | null {
  if (s.key === null) return null;
  const w = spec.words[s.key];
  return SORT_WORDS.sortedBy(w.name, s.dir === 'asc' ? w.asc : w.desc, grouped === 'page' ? 'page' : grouped ? 'group' : null);
}
